const DEBUG_STORAGE_KEY = "wnav_debug_latency";
const TRACE_SCHEMA = "website_navigator_latency_trace_v1";
const MAX_EVENTS = 5000;
const MAX_REQUESTS = 800;
const MAX_ASSETS = 800;
const SENSITIVE_KEY_PARTS = ["email", "mail", "phone", "name", "content", "message", "summary", "token", "secret", "password", "file_name", "filename", "upload_url", "url"];

function nowMs() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function isoNow() {
  return new Date().toISOString();
}

function cleanText(value) {
  return String(value || "").trim();
}

function randomId(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return `${prefix}_${crypto.randomUUID()}`;
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function roundMs(value) {
  const number = Number(value || 0);
  return Math.round(number * 10) / 10;
}

function byteLength(value) {
  if (value === undefined || value === null) return 0;
  if (typeof value === "string") return new Blob([value]).size;
  if (value instanceof FormData) {
    let total = 0;
    value.forEach((item) => {
      if (item instanceof File || item instanceof Blob) total += item.size;
      else total += byteLength(String(item));
    });
    return total;
  }
  try {
    return new Blob([JSON.stringify(value)]).size;
  } catch {
    return 0;
  }
}

function stableStringify(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(",")}}`;
}

function smallHash(value) {
  const text = typeof value === "string" ? value : stableStringify(value);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `h:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function sensitiveKey(key) {
  const lowered = cleanText(key).toLowerCase();
  return SENSITIVE_KEY_PARTS.some((part) => lowered.includes(part));
}

function looksDateTime(value) {
  const text = cleanText(value);
  return text.length >= 10
    && text.charAt(4) === "-"
    && text.charAt(7) === "-"
    && !Number.isNaN(Number(text.slice(0, 4)))
    && !Number.isNaN(Number(text.slice(5, 7)))
    && !Number.isNaN(Number(text.slice(8, 10)));
}

function enumLike(value) {
  const text = cleanText(value);
  if (text.length < 2 || text.length > 64) return false;
  return Array.from(text).every((char) => {
    const code = char.charCodeAt(0);
    return (code >= 65 && code <= 90)
      || (code >= 97 && code <= 122)
      || char === "_"
      || char === "."
      || char === "/"
      || char === "-";
  });
}

function jsonFromBody(body) {
  if (!body || typeof body !== "string") return null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function valueShape(value, depth = 0, key = "") {
  if (value === null) return { type: "null" };
  if (value === undefined) return { type: "undefined" };
  if (typeof value === "string") {
    const shape = { type: "string", length: value.length };
    if (sensitiveKey(key)) shape.redacted = true;
    if (looksDateTime(value)) shape.format_like = "datetime";
    if (enumLike(value) && !sensitiveKey(key)) shape.enum_like = true;
    return shape;
  }
  if (typeof value === "number") return { type: "number" };
  if (typeof value === "boolean") return { type: "boolean" };
  if (Array.isArray(value)) {
    const shape = { type: "array", length: value.length };
    if (depth < 3 && value.length) shape.sample = valueShape(value[0], depth + 1, key);
    return shape;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value);
    const shape = { type: "object", key_count: keys.length, keys: {} };
    if (depth >= 3) return shape;
    keys.slice(0, 40).forEach((itemKey) => {
      shape.keys[itemKey] = valueShape(value[itemKey], depth + 1, itemKey);
    });
    return shape;
  }
  return { type: typeof value };
}

function topLevelKeys(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? Object.keys(value).slice(0, 80) : [];
}

function knownCounts(value) {
  const output = {};
  if (!value || typeof value !== "object") return output;
  [
    "components",
    "component_updates",
    "jobs",
    "slots",
    "staff_options",
    "hints",
    "questions",
    "files",
    "services",
    "patches",
  ].forEach((key) => {
    if (Array.isArray(value[key])) output[`${key}_count`] = value[key].length;
  });
  if (value.state_patch && typeof value.state_patch === "object") Object.assign(output, knownCounts(value.state_patch));
  if (value.payload && typeof value.payload === "object") Object.assign(output, knownCounts(value.payload));
  return output;
}

function safeUrlPath(raw) {
  try {
    const parsed = new URL(raw, window.location.origin);
    return parsed.pathname;
  } catch {
    return String(raw || "").split("?")[0];
  }
}

function readDebugFlag() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("wnav_debug_latency") === "1") {
      window.localStorage.setItem(DEBUG_STORAGE_KEY, "1");
      return true;
    }
    if (params.get("wnav_debug_latency") === "0") {
      window.localStorage.removeItem(DEBUG_STORAGE_KEY);
      return false;
    }
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function parseServerTiming(value) {
  const output = {};
  cleanText(value).split(",").forEach((part) => {
    const chunks = part.trim().split(";");
    const name = cleanText(chunks.shift());
    if (!name) return;
    const durChunk = chunks.find((chunk) => chunk.trim().startsWith("dur="));
    if (!durChunk) return;
    const duration = Number(durChunk.split("=", 2)[1]);
    if (Number.isFinite(duration)) output[name] = roundMs(duration);
  });
  return output;
}

function inferArea(path, options = {}) {
  if (options.debugArea) return options.debugArea;
  const actionId = cleanText(options.actionId);
  if (actionId.startsWith("scheduling.")) return "booking";
  if (actionId.startsWith("website_check.")) return "website_check";
  if (actionId.startsWith("inquiry.")) return "contact";
  if (actionId.startsWith("upload.")) return "upload";
  if (path.includes("site-assist")) return "site_assist";
  if (path.includes("website-checks")) return "website_check";
  if (path.includes("uploads")) return "upload";
  if (path.includes("bootstrap")) return "bootstrap";
  if (path.includes("messages/stream")) return "stream";
  return "api";
}

function inferOperation(path, options = {}) {
  const actionId = cleanText(options.actionId);
  if (options.operationId) return options.operationId;
  if (actionId) return actionId;
  if (path.includes("bootstrap")) return "bootstrap";
  if (path.includes("site-assist")) return "site_assist";
  if (path.includes("website-checks")) return "website_check.state";
  if (path.includes("uploads")) return "upload";
  if (path.includes("messages")) return "message";
  return "api";
}

export function createWebsiteNavigatorLatencyDebug() {
  const enabled = readDebugFlag();
  const state = {
    enabled,
    traceId: randomId("wnav_trace"),
    openAt: nowMs(),
    createdAt: isoNow(),
    sequence: 0,
    events: [],
    requests: [],
    marks: [],
    requestHashes: new Map(),
    responseHashes: new Map(),
    inflight: new Map(),
  };

  const api = {
    enabled,
    traceId: () => state.traceId,
    startOpen(details = {}) {
      if (!state.enabled) return;
      state.traceId = randomId("wnav_trace");
      state.openAt = nowMs();
      state.createdAt = isoNow();
      state.events = [];
      state.requests = [];
      state.marks = [];
      state.requestHashes = new Map();
      state.responseHashes = new Map();
      state.inflight = new Map();
      this.event("navigator.open.trace_start", { area: "navigator_core", phase: "frontend", details });
    },
    operation(prefix) {
      state.sequence += 1;
      return `${cleanText(prefix) || "op"}:${state.sequence}`;
    },
    event(name, options = {}) {
      if (!state.enabled) return null;
      const event = {
        id: randomId("evt"),
        ts: isoNow(),
        t_rel_ms: roundMs(nowMs() - state.openAt),
        trace_id: state.traceId,
        operation_id: cleanText(options.operationId),
        parent_id: cleanText(options.parentId),
        area: cleanText(options.area || "navigator_core"),
        phase: cleanText(options.phase || "frontend"),
        event: cleanText(name),
        duration_ms: options.durationMs === undefined ? null : roundMs(options.durationMs),
        status: cleanText(options.status || "ok"),
        details: options.details || {},
      };
      state.events.push(event);
      if (state.events.length > MAX_EVENTS) state.events.shift();
      return event.id;
    },
    start(name, options = {}) {
      if (!state.enabled) return null;
      const token = {
        name,
        startedAt: nowMs(),
        operationId: cleanText(options.operationId),
        area: cleanText(options.area || "navigator_core"),
        phase: cleanText(options.phase || "frontend"),
        details: options.details || {},
      };
      this.event(`${name}.start`, { ...options, operationId: token.operationId, area: token.area, phase: token.phase, details: token.details });
      return token;
    },
    end(token, options = {}) {
      if (!state.enabled || !token) return;
      this.event(`${token.name}.end`, {
        operationId: options.operationId || token.operationId,
        area: options.area || token.area,
        phase: options.phase || token.phase,
        status: options.status || "ok",
        durationMs: nowMs() - token.startedAt,
        details: { ...(token.details || {}), ...(options.details || {}) },
      });
    },
    mark(name, details = {}) {
      if (!state.enabled) return;
      const mark = {
        id: randomId("mark"),
        ts: isoNow(),
        t_rel_ms: roundMs(nowMs() - state.openAt),
        name: cleanText(name),
        details,
      };
      state.marks.push(mark);
      this.event(name, { area: "debug_export", phase: "frontend", details });
    },
    requestStart(path, options = {}) {
      if (!state.enabled) return null;
      const bodyJson = jsonFromBody(options.body);
      const requestShape = bodyJson ? valueShape(bodyJson) : valueShape(options.body);
      const requestHash = smallHash(requestShape);
      const operationId = inferOperation(path, options);
      const area = inferArea(path, options);
      const requestKey = `${cleanText(options.method || "GET").toUpperCase()} ${safeUrlPath(path)} ${requestHash}`;
      const duplicateRequestHashCount = (state.requestHashes.get(requestHash) || 0) + 1;
      state.requestHashes.set(requestHash, duplicateRequestHashCount);
      const inflightCount = (state.inflight.get(requestKey) || 0) + 1;
      state.inflight.set(requestKey, inflightCount);
      if (inflightCount > 1) {
        this.event("api.request.inflight_duplicate", {
          operationId,
          area,
          phase: "api_client",
          details: { path: safeUrlPath(path), inflight_count: inflightCount, request_hash: requestHash },
        });
      }
      const token = {
        request_id: randomId("req"),
        client_event_id: randomId("client_evt"),
        trace_id: state.traceId,
        operation_id: operationId,
        area,
        path,
        safe_path: safeUrlPath(path),
        method: cleanText(options.method || "GET").toUpperCase(),
        startedAt: nowMs(),
        requestKey,
        request_hash: requestHash,
        request_shape: requestShape,
        request_bytes: byteLength(options.body),
        request_top_level_keys: topLevelKeys(bodyJson),
        request_counts: knownCounts(bodyJson),
        duplicate_request_hash_count: duplicateRequestHashCount,
      };
      this.event("api.fetch.start", {
        operationId,
        area,
        phase: "api_client",
        details: {
          method: token.method,
          path: token.safe_path,
          request_bytes: token.request_bytes,
          request_hash: token.request_hash,
          duplicate_request_hash_count: duplicateRequestHashCount,
          inflight_count: inflightCount,
        },
      });
      return token;
    },
    requestHeaders(token) {
      if (!state.enabled || !token) return {};
      return {
        "X-Wnav-Debug": "1",
        "X-Wnav-Trace-Id": token.trace_id,
        "X-Wnav-Operation-Id": token.operation_id,
        "X-Wnav-Client-Event-Id": token.client_event_id,
      };
    },
    requestEnd(token, result = {}) {
      if (!state.enabled || !token) return;
      const responseJson = result.data || jsonFromBody(result.responseText);
      const responseShape = valueShape(responseJson);
      const responseHash = smallHash(responseShape);
      const duplicateResponseHashCount = (state.responseHashes.get(responseHash) || 0) + 1;
      state.responseHashes.set(responseHash, duplicateResponseHashCount);
      const totalMs = nowMs() - token.startedAt;
      const serverTiming = parseServerTiming(result.serverTiming || "");
      const request = {
        request_id: cleanText(result.requestId) || token.request_id,
        trace_id: token.trace_id,
        operation_id: token.operation_id,
        method: token.method,
        path: token.safe_path,
        action_id: cleanText(result.actionId),
        status: Number(result.status || 0),
        total_ms: roundMs(totalMs),
        browser: {
          queued_ms: null,
          dns_ms: null,
          connect_ms: null,
          tls_ms: null,
          request_upload_ms: null,
          ttfb_ms: null,
          response_download_ms: null,
          json_parse_ms: result.jsonParseMs === undefined ? null : roundMs(result.jsonParseMs),
        },
        payload: {
          request_bytes: token.request_bytes,
          response_bytes: byteLength(result.responseText),
          request_hash: token.request_hash,
          response_hash: responseHash,
          request_top_level_keys: token.request_top_level_keys,
          response_top_level_keys: topLevelKeys(responseJson),
          request_shape: token.request_shape,
          response_shape: responseShape,
          request_counts: token.request_counts,
          response_counts: knownCounts(responseJson),
          duplicate_request_hash_count: token.duplicate_request_hash_count,
          duplicate_response_hash_count: duplicateResponseHashCount,
        },
        server_timing: serverTiming,
      };
      const resource = this.matchResource(token);
      if (resource) request.browser = { ...request.browser, ...resource };
      state.requests.push(request);
      if (state.requests.length > MAX_REQUESTS) state.requests.shift();
      state.inflight.set(token.requestKey, Math.max(0, (state.inflight.get(token.requestKey) || 1) - 1));
      this.event("api.fetch.end", {
        operationId: token.operation_id,
        area: token.area,
        phase: "api_client",
        status: result.ok === false ? "error" : "ok",
        durationMs: totalMs,
        details: {
          method: token.method,
          path: token.safe_path,
          status: request.status,
          request_bytes: request.payload.request_bytes,
          response_bytes: request.payload.response_bytes,
          request_hash: request.payload.request_hash,
          response_hash: request.payload.response_hash,
          duplicate_response_hash_count: duplicateResponseHashCount,
          server_timing: serverTiming,
        },
      });
    },
    requestError(token, error = {}) {
      if (!state.enabled || !token) return;
      state.inflight.set(token.requestKey, Math.max(0, (state.inflight.get(token.requestKey) || 1) - 1));
      this.event("api.fetch.error", {
        operationId: token.operation_id,
        area: token.area,
        phase: "api_client",
        status: "error",
        durationMs: nowMs() - token.startedAt,
        details: {
          method: token.method,
          path: token.safe_path,
          status: error.status || 0,
          detail: cleanText(error.detail || error.message || "request_failed"),
        },
      });
    },
    matchResource(token) {
      if (typeof performance === "undefined" || !performance.getEntriesByType) return null;
      const entries = performance.getEntriesByType("resource")
        .filter((entry) => safeUrlPath(entry.name) === token.safe_path)
        .sort((left, right) => Math.abs(left.startTime - token.startedAt) - Math.abs(right.startTime - token.startedAt));
      const entry = entries[0];
      if (!entry) return null;
      const requestUploadMs = entry.responseStart && entry.requestStart ? entry.responseStart - entry.requestStart : null;
      return {
        queued_ms: entry.requestStart ? roundMs(entry.requestStart - entry.startTime) : null,
        dns_ms: entry.domainLookupEnd ? roundMs(entry.domainLookupEnd - entry.domainLookupStart) : null,
        connect_ms: entry.connectEnd ? roundMs(entry.connectEnd - entry.connectStart) : null,
        tls_ms: entry.secureConnectionStart ? roundMs(entry.connectEnd - entry.secureConnectionStart) : null,
        request_upload_ms: requestUploadMs === null ? null : roundMs(requestUploadMs),
        ttfb_ms: entry.responseStart ? roundMs(entry.responseStart - entry.requestStart) : null,
        response_download_ms: entry.responseEnd && entry.responseStart ? roundMs(entry.responseEnd - entry.responseStart) : null,
      };
    },
    collectAssets() {
      if (!state.enabled || typeof performance === "undefined" || !performance.getEntriesByType) return [];
      const seen = new Set();
      const assets = performance.getEntriesByType("resource").map((entry) => {
        const urlPath = safeUrlPath(entry.name);
        const key = `${urlPath}:${entry.startTime}:${entry.initiatorType}`;
        if (seen.has(key)) return null;
        seen.add(key);
        return {
          url_path: urlPath,
          initiator_type: entry.initiatorType || "",
          total_ms: roundMs(entry.duration),
          transfer_size: Number(entry.transferSize || 0),
          encoded_body_size: Number(entry.encodedBodySize || 0),
          decoded_body_size: Number(entry.decodedBodySize || 0),
          from_cache_hint: Boolean(entry.transferSize === 0 && entry.decodedBodySize > 0),
          started_after_open_ms: roundMs(entry.startTime - state.openAt),
          navigator_related: urlPath.includes("website") || urlPath.includes("navigator") || entry.startTime >= state.openAt,
        };
      }).filter(Boolean);
      return assets.slice(-MAX_ASSETS);
    },
    summary() {
      const events = state.events;
      const requests = state.requests;
      const assets = this.collectAssets();
      const eventByName = (name) => events.find((event) => event.event === name);
      const durationFromOpen = (name) => {
        const event = eventByName(name);
        return event ? event.t_rel_ms : null;
      };
      const slowestOperations = Object.entries(requests.reduce((acc, request) => {
        acc[request.operation_id] = (acc[request.operation_id] || 0) + request.total_ms;
        return acc;
      }, {})).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([operation, duration]) => [operation, roundMs(duration)]);
      const stageTotals = {};
      requests.forEach((request) => {
        Object.entries(request.server_timing || {}).forEach(([key, value]) => {
          stageTotals[key] = (stageTotals[key] || 0) + Number(value || 0);
        });
      });
      const duplicateRequests = requests
        .filter((request) => Number(request.payload?.duplicate_request_hash_count || 0) > 1)
        .map((request) => ({
          path: request.path,
          operation_id: request.operation_id,
          duplicate_request_hash_count: request.payload.duplicate_request_hash_count,
          request_hash: request.payload.request_hash,
        }));
      const duplicatePayloads = requests
        .filter((request) => Number(request.payload?.duplicate_response_hash_count || 0) > 1)
        .map((request) => ({
          path: request.path,
          operation_id: request.operation_id,
          duplicate_response_hash_count: request.payload.duplicate_response_hash_count,
          response_hash: request.payload.response_hash,
        }));
      const parallelSameKeyRequests = events
        .filter((event) => event.event === "api.request.inflight_duplicate")
        .map((event) => ({
          operation_id: event.operation_id,
          path: event.details?.path,
          inflight_count: event.details?.inflight_count,
          request_hash: event.details?.request_hash,
        }));
      const largestRequests = [...requests]
        .sort((left, right) => Number(right.payload?.request_bytes || 0) - Number(left.payload?.request_bytes || 0))
        .slice(0, 10)
        .map((request) => ({
          path: request.path,
          operation_id: request.operation_id,
          request_bytes: request.payload.request_bytes,
          request_counts: request.payload.request_counts,
        }));
      const largestResponses = [...requests]
        .sort((left, right) => Number(right.payload?.response_bytes || 0) - Number(left.payload?.response_bytes || 0))
        .slice(0, 10)
        .map((request) => ({
          path: request.path,
          operation_id: request.operation_id,
          response_bytes: request.payload.response_bytes,
          response_counts: request.payload.response_counts,
        }));
      const summary = {
        open_to_shell_visible_ms: durationFromOpen("navigator.open.shell_visible"),
        open_to_first_interactive_ms: durationFromOpen("navigator.open.first_interactive"),
        open_to_bootstrap_ready_ms: durationFromOpen("bootstrap.end"),
        open_to_stream_done_ms: durationFromOpen("stream.event.done"),
        open_to_site_assist_ready_ms: durationFromOpen("site_assist.request.end"),
        slowest_operations: slowestOperations,
        slowest_requests: [...requests].sort((left, right) => right.total_ms - left.total_ms).slice(0, 12),
        slowest_backend_stages: Object.entries(stageTotals).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([stage, duration]) => [stage, roundMs(duration)]),
        slowest_db_stages: Object.entries(stageTotals).filter(([stage]) => String(stage).includes("db") || String(stage).includes("sql")).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([stage, duration]) => [stage, roundMs(duration)]),
        largest_requests: largestRequests,
        largest_responses: largestResponses,
        largest_assets: [...assets].sort((left, right) => right.decoded_body_size - left.decoded_body_size).slice(0, 12),
        duplicate_requests: duplicateRequests,
        duplicate_payloads: duplicatePayloads,
        parallel_same_key_requests: parallelSameKeyRequests,
        cache_hits: events.filter((event) => event.event.includes("cache_hit")).length,
        cache_misses: events.filter((event) => event.event.includes("cache_miss")).length,
        inflight_hits: events.filter((event) => event.event.includes("inflight")).length,
        render_hotspots: events.filter((event) => event.phase === "render" && Number(event.duration_ms || 0) > 16).sort((left, right) => Number(right.duration_ms || 0) - Number(left.duration_ms || 0)).slice(0, 12),
      };
      return summary;
    },
    recommendations() {
      const summary = this.summary();
      const output = [];
      if (summary.duplicate_requests.length) output.push("Dedupe identical navigator requests while an equivalent request is in flight.");
      if (summary.parallel_same_key_requests.length) output.push("Inspect parallel same-key navigator requests for missing in-flight reuse.");
      if (summary.duplicate_payloads.length) output.push("Inspect duplicate response payloads for short-lived client or server cache opportunities.");
      if (summary.largest_responses.some((item) => Number(item.response_bytes || 0) > 30000)) output.push("Review large navigator responses for lazy loading or smaller payload contracts.");
      if (summary.largest_requests.some((item) => Number(item.request_bytes || 0) > 12000)) output.push("Review large navigator request bodies for unnecessary context or repeated fields.");
      if (summary.largest_assets.some((item) => Number(item.decoded_body_size || 0) > 150000)) output.push("Review large assets loaded near navigator open for lazy loading or preloading strategy.");
      if (summary.slowest_backend_stages.some(([stage]) => String(stage).includes("db"))) output.push("Inspect DB and pool timing for the slowest backend stages.");
      if (summary.render_hotspots.length) output.push("Inspect render hotspots where DOM work exceeds one frame.");
      return output;
    },
    export() {
      if (!state.enabled) return null;
      const payload = {
        schema: TRACE_SCHEMA,
        created_at: isoNow(),
        page: {
          origin: window.location.origin,
          path: window.location.pathname,
          language: document.documentElement.lang || "",
        },
        session: {
          trace_id: state.traceId,
          debug_mode: true,
          user_agent_family: navigator.userAgentData?.brands?.[0]?.brand || navigator.userAgent.split(" ")[0] || "unknown",
          viewport: { width: window.innerWidth, height: window.innerHeight },
        },
        events: state.events,
        requests: state.requests,
        assets: this.collectAssets(),
        marks: state.marks,
        summary: this.summary(),
        recommendations: this.recommendations(),
      };
      const filename = `website-navigator-latency-trace-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        URL.revokeObjectURL(link.href);
        link.remove();
      }, 0);
      return payload;
    },
    clear() {
      if (!state.enabled) return;
      state.events = [];
      state.requests = [];
      state.marks = [];
      state.requestHashes = new Map();
      state.responseHashes = new Map();
      state.inflight = new Map();
      this.event("debug_export.clear", { area: "debug_export", phase: "frontend" });
    },
  };
  if (enabled && typeof window !== "undefined") window.__wnavLatencyDebug = api;
  return api;
}
